import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { worksApi } from '@/lib/api/works';
import { Work } from '@/lib/types/works';
import { useWorksStore as useWorksCountStore } from '@/lib/stores/works-store';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import Logger from '@/lib/logger';
// Constants
export const MAX_FREE_WORKS = 5;

// Query keys for React Query - More granular for better cache control
export const worksKeys = {
  all: ['works'] as const,
  lists: () => [...worksKeys.all, 'list'] as const,
  list: (userId: string) => [...worksKeys.lists(), userId] as const,
  counts: () => [...worksKeys.all, 'count'] as const,
  count: (userId: string) => [...worksKeys.counts(), userId] as const,
  // Add more specific keys for individual operations
  operations: () => [...worksKeys.all, 'operations'] as const,
  reorder: (userId: string) => [...worksKeys.operations(), 'reorder', userId] as const,
  delete: (userId: string) => [...worksKeys.operations(), 'delete', userId] as const,
};

export function useWorksCount(userId: string | null) {
  const { totalWorksCount, setTotalWorksCount } = useWorksCountStore();
  
  const query = useQuery({
    queryKey: worksKeys.count(userId || ''),
    queryFn: async () => {
      if (!userId) return 0;

      const { data, error } = await supabase
        .from('profiles')
        .select('total_works_count')
        .eq('id', userId)
        .single();

      if (error) throw error;
      const count = data.total_works_count || 0;
      // Update the store with the fetched count
      setTotalWorksCount(count);
      return count;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 10, // 10 minutes - increased from 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus for counts
  });
  
  // If we have a count in the store but the query is still loading, return the store value
  if (query.isLoading && userId && totalWorksCount > 0) {
    return {
      ...query,
      data: totalWorksCount
    };
  }
  
  return query;
}

export function useWorks(userId: string | null) {
  const queryClient = useQueryClient();
  // Don't get setWorks from the global store - we'll manage works per-component
  const { setTotalWorksCount } = useWorksCountStore();
  
  const worksQuery = useQuery({
    queryKey: worksKeys.list(userId || ''),
    queryFn: async () => {
      const works = await worksApi.getWorks(userId!);
      // Remove the global store update
      return works;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 15, // 15 minutes - increased from 10 minutes
    gcTime: 1000 * 60 * 60, // 1 hour - keep works in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data is fresh
  });

  const updateWorksCount = async (change: number) => {
    if (!userId) return;
    
    // Get current count from cache first if available
    const cachedCount = queryClient.getQueryData<number>(worksKeys.count(userId));
    const newCount = Math.max(0, (cachedCount ?? 0) + change);
    
    // Optimistically update the cache and store
    queryClient.setQueryData(worksKeys.count(userId), newCount);
    setTotalWorksCount(newCount);
    
    try {
      // Then update the database
      await supabase
        .from('profiles')
        .update({ total_works_count: newCount })
        .eq('id', userId);
    } catch (error) {
      Logger.error('Error updating works count:', error);
      // We don't revert the optimistic update here as we'll refetch on error
      queryClient.invalidateQueries({ queryKey: worksKeys.count(userId) });
    }
  };

  // Add a function to update the cache for works created directly via Supabase
  const addWorkToCache = async (work: Work) => {
    if (!userId) return;
    
    // Check if the work already exists in the cache to avoid duplicate entries
    queryClient.setQueryData<Work[]>(
      worksKeys.list(userId),
      (old) => {
        if (!old) return [work];
        
        // Check if this work is already in the cache
        const existingWorkIndex = old.findIndex(w => w.id === work.id);
        
        if (existingWorkIndex >= 0) {
          // Work exists, update it
          const newWorks = [...old];
          newWorks[existingWorkIndex] = work;
          return newWorks;
        } else {
          // Work doesn't exist, add it to the beginning
          return [work, ...old];
        }
      }
    );
    
    // Increment works count
    await updateWorksCount(1);
  };

  const createWorkMutation = useMutation({
    mutationFn: worksApi.createWork,
    onMutate: async (newWorkData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: worksKeys.list(userId!) });
      
      // Snapshot the previous values
      const previousWorks = queryClient.getQueryData<Work[]>(worksKeys.list(userId!));
      
      // Create a temporary work with optimistic data
      const optimisticWork: Work = {
        ...newWorkData,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        assets: [],
        // Add other required properties with default values
        is_ready: false,
        slug: `temp-slug-${Date.now()}` // Use a temporary slug instead of null
      } as Work;
      
      // Optimistically update the cache
      queryClient.setQueryData<Work[]>(
        worksKeys.list(userId!),
        (old) => old ? [optimisticWork, ...old] : [optimisticWork]
      );
      
      // Return the previous values
      return { previousWorks };
    },
    onSuccess: async (newWork) => {
      // Update works list with actual data from server
      queryClient.setQueryData<Work[]>(
        worksKeys.list(userId!),
        (old) => {
          if (!old) return [newWork];
          // Filter out any temporary version of this work and add the real one
          return [newWork, ...old.filter(w => !w.id.startsWith('temp-'))];
        }
      );
      
      // Increment works count
      await updateWorksCount(1);
      
      toast.success('Work created successfully');
    },
    onError: (error, _, context) => {
      Logger.error('Error creating work:', error);
      toast.error('Failed to create work');
      
      // Revert to previous state
      if (context?.previousWorks) {
        queryClient.setQueryData(worksKeys.list(userId!), context.previousWorks);
      }
    },
  });

  const deleteWorkMutation = useMutation({
    mutationFn: worksApi.deleteWork,
    onMutate: async (workId) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: worksKeys.list(userId!) });
      
      // Snapshot the previous value
      const previousWorks = queryClient.getQueryData<Work[]>(worksKeys.list(userId!));
      
      // Optimistically update only the works list
      queryClient.setQueryData<Work[]>(worksKeys.list(userId!), old => {
        const filtered = old?.filter(work => work.id !== workId) || [];
        return filtered;
      });
      
      // Return a context with the previous value
      return { previousWorks };
    },
    onSuccess: async (_, workId) => {
      Logger.info(`Work deleted successfully: ${workId}`);
      toast.success('Work deleted successfully');
      
      // Only invalidate the works count since the database trigger updates it
      // The works list is already updated optimistically
      queryClient.invalidateQueries({ 
        queryKey: worksKeys.count(userId!),
        refetchType: "active"
      });
    },
    onError: (error, workId, context) => {
      Logger.error('Error deleting work:', error);
      toast.error('Failed to delete work');
      
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousWorks) {
        queryClient.setQueryData(worksKeys.list(userId!), context.previousWorks);
      }
      
      // Only invalidate on error to ensure data consistency
      queryClient.invalidateQueries({ 
        queryKey: worksKeys.list(userId!),
        refetchType: "active"
      });
    },
  });

  const optimizeAssetMutation = useMutation({
    mutationFn: worksApi.optimizeAsset,
    onSuccess: () => {
      toast.success('Asset optimization started');
    },
    onError: (error) => {
      Logger.error('Error optimizing asset:', error);
      toast.error('Failed to optimize asset');
    },
  });

  return {
    works: worksQuery.data,
    isLoading: worksQuery.isLoading,
    error: worksQuery.error,
    createWork: createWorkMutation.mutate,
    deleteWork: deleteWorkMutation.mutate,
    optimizeAsset: optimizeAssetMutation.mutate,
    addWorkToCache,
    updateWorksCount,
  };
}